// IMP-220: voucher template 데이터 조회 레이어 — SmartDentAction 에서 추출(action↔DDB 직접 호출 제거). IMP-210 companyHierarchyService 패턴.
import { DoDB } from './ddb';
import { GetManagerGroupIdByCompanyId } from './managerGroupResolver';
import { getParents } from './companyHierarchyService';
import { TABLE_RAYTEAMS_PRODUCT, TABLE_RAYTEAMS_MANAGER_GROUP, SK_VOUCHERS_DEFAULT } from './rayteamsTables';
import { ORG_BRANCH, ORG_HQ } from '../config/appConstants';
import { LICENSE_PRODUCT_SMARTDENT, DEFAULT_LICENSE_PRODUCT } from '../actions/constants/LicenseProductConstant';

// IMP-214: SMARTDent 패키지 ID(rayteams-product 테이블 PK) — HQ 기본 voucher template 조회에 쓴다.
// 기본값은 LicenseProductConstant.js 의 LICENSE_PRODUCT_SMARTDENT.packageIds[0].
// 사용자 명시 (2026-05-13): HQ vouchers:default 는 manager-group 이 아닌 rayteams-product
// 테이블에 smartdent_standalone 상품 단위로 저장된다. Branch 조직이 Reset to Default 시
// walk-up 없이 이 상품 ID 로 직접 조회. Subsidiary/Dealer 의 walk-up 결과 부재 시 fallback.
//
// PR #239 review (Sec/CQ/Arch Blocker 1124-1127):
//   - prod 운영 시 실 PK 가 다른 경우 환경변수 `REACT_APP_HQ_VOUCHER_PRODUCT_ID` 로 override.
//   - default 값은 사용자 명시 값 유지 (현재 RAY 시스템은 RFC 4122 UUID 가 아닌 임의 string PK).
//   - 비어있지 않음 가드만 적용 (형식 검증은 PK 가 UUID 아니므로 제외).
//   - 향후 BE 엔드포인트 위임으로 FE 가 HQ PK 모르도록 이관 권장 (별도 IMP).
// PR #239 review (Sec test Sug:649): 테스트 파일 sync 보장을 위해 export.
// production 환경변수 override 후 effective 값도 함께 export.
// PR #395 review (CQ Sug): override 경로(REACT_APP_HQ_VOUCHER_PRODUCT_ID)는 모듈 로드
//   시점에 상수가 즉시 평가되므로 Jest 단위 검증 불가(jest.resetModules + isolateModules
//   없이는 런타임 주입 효과 없음). prod 환경변수 적용 결과는 수동 smoke test 로 확인한다.
export const SMARTDENT_PRODUCT_ID_HQ_VOUCHER_DEFAULT = LICENSE_PRODUCT_SMARTDENT.packageIds[0];
export const SMARTDENT_PRODUCT_ID_HQ_VOUCHER = (
    typeof process !== 'undefined'
        && process.env
        && typeof process.env.REACT_APP_HQ_VOUCHER_PRODUCT_ID === 'string'
        && process.env.REACT_APP_HQ_VOUCHER_PRODUCT_ID.trim().length > 0
)
    ? process.env.REACT_APP_HQ_VOUCHER_PRODUCT_ID.trim()
    : SMARTDENT_PRODUCT_ID_HQ_VOUCHER_DEFAULT;

// IMP-191 follow-up (2026-05-12): 본인의 직속 상위 회사 ID 를 반환.
// 본인이 최상위(HQ) 면 본인 ID 그대로. RAYGuard 컨벤션:
//   - 법인(Subsidiary) 의 부모 = HQ
//   - 딜러 의 부모 = 법인
//   - HQ 는 부모 없음 → 본인 ID 반환 (HQ 본인 vouchers:default 조회로 폴백)
// GetParents 응답이 본인 entry 를 포함할 수도 있고 부모 entry 만 반환할 수도 있어
// 두 케이스 모두 지원. 네트워크 실패 시에도 본인 ID 반환으로 안전 fallback.
//
// M1 (CWE-639/200 보강): companyId 인자를 selfEntry 매칭의 기준으로 명시 사용.
// GetParents() 는 localStorage user.companyId 기반이므로, 응답 리스트에서 인자와
// 일치하는 entry 를 명시적으로 식별하여 ancestor chain 오인식을 차단.
// M3 (Arch): IMP-210 — action↔action 의존 제거. 부모 조회는 service layer
// companyHierarchyService.getParents() 직접 호출(CompanyAction 경유 제거).
/**
 * 주어진 companyId 의 직속 상위 회사 ID 를 반환합니다.
 * 상위가 없으면(HQ) companyId 그대로 반환합니다.
 *
 * @param {string} companyId - 상위 조직을 탐색할 기준 회사 ID (인자 우선 적용)
 * @returns {Promise<string>} 직속 상위 companyId, 또는 상위 없을 시 companyId 본인
 */
const resolveParentOrSelfCompanyId = async (companyId) => {
    try {
        const parents = await getParents();
        const list = Array.isArray(parents) ? parents : [];
        // 1순위: GetParents 가 본인 entry 를 포함하는 경우.
        // companyId 인자와 entry.code 를 명시적으로 비교하여 ancestor chain 오인식 차단 (M1).
        const selfEntry = list.find((x) => x?.code === companyId);
        if (selfEntry?.parentCompanyId) return selfEntry.parentCompanyId;
        // 2순위: GetParents 가 본인 미포함 + 부모 entry 만 반환하는 경우 — 본인 외 첫 entry
        const otherEntry = list.find((x) => x?.code && x?.code !== companyId);
        if (otherEntry?.code) return otherEntry.code;
        // 상위 없음(HQ) — 본인 ID 그대로
        return companyId;
    } catch (error) {
        console.warn(
            '[voucherTemplateRepository.resolveParentOrSelfCompanyId]',
            String(error?.message || error).slice(0, 200),
        );
        return companyId;
    }
};

// PR #239 review (Sec Minor 1144): record.deleted 를 일관된 strict 비교로 정규화.
// manager-voucher-db v1.0.0 § 6 의 deleted 필드는 boolean 이지만, 데이터 마이그레이션
// 과정에서 'true' / 'false' 문자열 또는 1 / 0 number 가 섞일 수 있음. 정확히 `=== true`
// 일 때만 삭제 처리하고, 그 외 truthy 값 (문자열 'false' 등) 은 정상 레코드로 간주한다.
const isVoucherRecordDeleted = (record) => record?.deleted === true;

// IMP-214: smartdent_standalone 상품 ID 로 rayteams-product/vouchers:default 를 직접 조회.
// deleted strict true 면 "없는 템플릿" 으로 간주하여 null 반환.
// FEAT-057: 제품별 HQ 고정 패키지 ID — env override 는 기본 제품(SMARTDent)에만 적용, packageIds 가 비면 null(폴백 없음).
const resolveHqVoucherProductId = (product) => {
    const p = product ?? DEFAULT_LICENSE_PRODUCT;
    if (p.key === DEFAULT_LICENSE_PRODUCT.key) return SMARTDENT_PRODUCT_ID_HQ_VOUCHER;
    return p.packageIds?.[0] ?? null;
};

const fetchVoucherByHqFixedId = async (product) => {
    const hqProductId = resolveHqVoucherProductId(product);
    if (!hqProductId) return null;
    const list = await DoDB('query', {
        TableName: TABLE_RAYTEAMS_PRODUCT,
        KeyConditionExpression: '#_id = :_id and #sk = :sk',
        ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
        ExpressionAttributeValues: {
            ':_id': hqProductId,
            ':sk': SK_VOUCHERS_DEFAULT,
        },
    });
    if (!Array.isArray(list) || list.length === 0) return null;
    const record = list[0];
    // manager-voucher-db v1.0.0 § 6: deleted === true → 없는 템플릿
    return isVoucherRecordDeleted(record) ? null : record;
};

// IMP-218: fetchDefaultVoucherTemplate → strategy pattern 분리 (SRP 정렬).
// 진실표(IMP-214 정의):
//   ORG_BRANCH              → fetchBranchTemplate  (HQ 고정 ID 직접 조회, walk-up 스킵)
//   ORG_HQ / undefined/null → fetchHqOwnTemplate   (walk-up 전용, fallback 없음)
//   그 외 (Subsidiary 등)   → fetchWithHqFallback  (walk-up + 결과 null → HQ fallback)
//
// IMP-191 follow-up: 직속 상위 회사(또는 HQ 본인) 의 manager group 에서
// vouchers:default 레코드를 조회하는 walk-up 공통 로직.

/**
 * [IMP-218] walk-up 전용 private helper.
 * resolveParentOrSelfCompanyId → GetManagerGroupIdByCompanyId → DoDB query 순서로 실행.
 * 각 단계 실패(null/empty) 또는 deleted === true 이면 null 반환.
 *
 * @param {string} companyId - 조회 기준 회사 ID
 * @returns {Promise<object|null>} vouchers:default 레코드, 없으면 null
 * @throws 호출자가 catch 처리해야 함 (DoDB / GetManagerGroupIdByCompanyId 예외 전파)
 */
const fetchWalkUpTemplate = async (companyId) => {
    const targetCompanyId = await resolveParentOrSelfCompanyId(companyId);
    if (!targetCompanyId) return null;
    const managerGroupId = await GetManagerGroupIdByCompanyId(targetCompanyId);
    if (!managerGroupId) return null;
    const list = await DoDB('query', {
        TableName: TABLE_RAYTEAMS_MANAGER_GROUP,
        KeyConditionExpression: '#_id = :_id and #sk = :sk',
        ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
        ExpressionAttributeValues: {
            ':_id': managerGroupId,
            ':sk': SK_VOUCHERS_DEFAULT,
        },
    });
    const walkUpRecord = Array.isArray(list) && list.length > 0 ? list[0] : null;
    // PR #239 review (CQ Minor 1195 + Arch Sug 1195): walk-up 결과 정규화 — deleted true 면 null.
    return isVoucherRecordDeleted(walkUpRecord) ? null : walkUpRecord;
};

// [IMP-218] 아래 3개 전략 함수는 단위 테스트(SmartDentActionVoucherStrategy.test.js 에서
// 이관 — voucherTemplateRepository.test.js)에서 독립 검증하기 위한 testability export 다.
// 다른 소스 파일에서 직접 import 하지 말 것
// (호출 진입점은 fetchDefaultVoucherTemplate dispatcher 하나로 유지). SmartDentAction
// 객체에는 포함하지 않는다.
/**
 * [IMP-218] Branch 전략: HQ 고정 ID 직접 조회 (walk-up 스킵).
 * companyId 는 시그니처 통일을 위해 선언하되 미사용.
 *
 * @param {string} _companyId - 미사용 (시그니처 통일)
 * @returns {Promise<object|null>}
 */
export const fetchBranchTemplate = async (_companyId, product) => fetchVoucherByHqFixedId(product);

/**
 * [IMP-218] HQ / legacy 전략: walk-up 전용, HQ fallback 없음.
 * org === ORG_HQ 및 org 미전달(undefined/null) 공통 적용.
 * GetVoucherTemplate 기존 동작(undefined 전달) 보존.
 *
 * @param {string} companyId - 조회 기준 회사 ID
 * @returns {Promise<object|null>}
 */
export const fetchHqOwnTemplate = async (companyId) => fetchWalkUpTemplate(companyId);

/**
 * [IMP-218] Subsidiary / Dealer 전략: walk-up 실행 후 결과 null 이면 HQ 고정 ID fallback.
 *
 * @param {string} companyId - 조회 기준 회사 ID
 * @returns {Promise<object|null>}
 */
export const fetchWithHqFallback = async (companyId, product) => {
    const walkUp = await fetchWalkUpTemplate(companyId);
    // fetchWalkUpTemplate 은 null 또는 레코드 객체만 반환(undefined 없음)하므로
    // strict `=== null` 로 충분 — 원본 `effectiveWalkUp === null` 비교와 동치.
    return walkUp === null ? fetchVoucherByHqFixedId(product) : walkUp;
};

/**
 * [IMP-218] org 값에 따라 3가지 전략 중 하나를 반환하는 private dispatcher.
 * 진실표:
 *   ORG_BRANCH              → fetchBranchTemplate
 *   ORG_HQ / undefined/null → fetchHqOwnTemplate  (fallback 없음)
 *   그 외                   → fetchWithHqFallback
 *
 * @param {string|undefined|null} org
 * @returns {function(companyId: string): Promise<object|null>}
 */
const resolveVoucherTemplateStrategy = (org) => {
    if (org === ORG_BRANCH) return fetchBranchTemplate;
    // IMP-214: org 미전달(undefined/null) 및 ORG_HQ → fallback 없음 (GetVoucherTemplate 기존 동작 보존)
    if (!org || org === ORG_HQ) return fetchHqOwnTemplate;
    // Subsidiary / Dealer 등 명시적 비-HQ 비-Branch → walk-up + HQ fallback
    return fetchWithHqFallback;
};

// IMP-214 확장: org 파라미터 추가.
//   - org === ORG_BRANCH  → HQ 고정 ID 직접 조회 (walk-up 스킵). deleted truthy → null.
//   - org === ORG_HQ      → 기존 walk-up (자기 자신 manager group).
//   - org === ORG_SUBSIDIARY 또는 기타 비-HQ 비-Branch (Dealer 등)
//                         → 기존 walk-up. 결과 null 또는 deleted truthy → HQ 고정 ID fallback.
//   - org 미전달(undefined/null) → 기존 walk-up (HQ fallback 없음. GetVoucherTemplate 기존 동작 유지).
/**
 * [IMP-218] thin dispatcher. 기존 시그니처(companyId, org)와 호출부 그대로 유지.
 * 내부적으로 resolveVoucherTemplateStrategy 로 전략 선택 후 실행.
 *
 * @param {string} companyId - 조회 기준 회사 ID
 * @param {string|undefined} [org] - companyInfo.org 값 (ORG_BRANCH / ORG_HQ / ORG_SUBSIDIARY / 기타)
 * @param {object} [product] - 라이선스 제품(미전달 시 SMARTDent). HQ 고정 ID 폴백 대상 결정(FEAT-057)
 * @returns {Promise<object|null>} vouchers:default 레코드, 없으면 null
 * @throws 호출자가 catch 처리해야 함 (DoDB / GetManagerGroupIdByCompanyId 예외 전파)
 */
export const fetchDefaultVoucherTemplate = (companyId, org, product) =>
    resolveVoucherTemplateStrategy(org)(companyId, product);
